// Angular Modules
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// Library Modules
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';

// Components
import { AppComponent } from './app.component';
import { NavBarTopComponent } from './components/nav-bar-top/nav-bar-top.component';
import { LoginModalComponent } from './components/login-modal/login-modal.component';
import { AdminComponent } from './components/admin/admin.component';
import { ApplicationuserComponent } from './components/applicationuser/applicationuser.component';
import { AdminAppropriatorsComponent } from './components/admin/admin-appropriators/admin-appropriators.component';
import { AddAppropriatorModalComponent } from './components/admin/admin-appropriators/add-appropriator-modal/add-appropriator-modal.component';
import { EditAppropriatorModalComponent } from './components/admin/admin-appropriators/edit-appropriator-modal/edit-appropriator-modal.component';

// Routed Pages
import { HomeComponent } from './routed-pages/home/home.component';
import { AnalysisComponent } from './routed-pages/analysis/analysis.component';
import { RecordsComponent } from './routed-pages/records/records.component';
import { DemandModule } from './routed-pages/demand/demand.module';
import { DemandReductionModule } from './routed-pages/demand-reduction/demand-reduction.module';
import { PermitsModule } from './routed-pages/permits/permits.module';
import { NewPermitModule } from './routed-pages/new-permit/new-permit.module';
import { SeasonalRankingModule } from './routed-pages/seasonal-ranking/seasonal-ranking.module';
import { SponsorPortalModule } from './routed-pages/sponsor-portal/sponsor-portal.module';
import { EvaluateOutcomesModule } from './routed-pages/evaluate-outcomes/evaluate-outcomes.module';
import { AccountSettingsModule } from './routed-pages/account-settings/account-settings.module';
import { EventsModule } from './routed-pages/events/events.module';
import { CommunicationsModule } from './routed-pages/communications/communications.module';
import { OutcomesModule } from './routed-pages/outcomes/outcomes.module';

// Services/Other
import { shouldSendBearerToken } from './../../src/main-url';
import { LoginGuard } from './guards/login.guard';
import { I1 } from './interceptors/interceptor';
import { FiledownloadComponent } from './routed-pages/filedownload/filedownload.component';
import { DatePipe } from '@angular/common';
import { AdminChangepasswordComponent } from './components/admin-changepassword/admin-changepassword.component';
import { LoginComponent } from './components/login/login.component';
import { ConfigurationService } from './services/configuration.service';
import { LoadingModule } from './components/loading/loading.module';
import { TermsConditionsComponent } from './components/terms-conditions/terms-conditions.component';

export function initializeApp(configurationService: ConfigurationService) {
  return () => {
    console.info("initializeApp called!");
    return configurationService.loadConfiguration();
  };
};

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    AnalysisComponent,
    RecordsComponent,
    NavBarTopComponent,
    LoginModalComponent,
    AdminComponent,
    FiledownloadComponent,
    ApplicationuserComponent,
    AdminChangepasswordComponent,
    LoginComponent,
    AdminAppropriatorsComponent,
    AddAppropriatorModalComponent,
    EditAppropriatorModalComponent,
    TermsConditionsComponent,
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    NgMultiSelectDropDownModule,
    HttpClientModule,
    DemandModule,
    DemandReductionModule,
    PermitsModule,
    NewPermitModule,
    EvaluateOutcomesModule,
    SeasonalRankingModule,
    SponsorPortalModule,
    AccountSettingsModule,
    EventsModule,
    CommunicationsModule,
    OutcomesModule,
    OAuthModule.forRoot({ //https://github.com/manfredsteyer/angular-oauth2-oidc
      resourceServer: {
        //Due to bug (https://github.com/manfredsteyer/angular-oauth2-oidc/issues/593)
        //must specify empty allowedUrls in order for customUrlValidation to work.
        allowedUrls: [],
        customUrlValidation: shouldSendBearerToken,
        sendAccessToken: true
      }
    }),
    FormsModule,
    ReactiveFormsModule,
    NgxChartsModule,
    BrowserAnimationsModule,
    LeafletModule.forRoot(),
    NgbModule,
    LoadingModule,
    ToastrModule.forRoot(),
    RouterModule.forRoot([
      { path: 'login', component: LoginComponent, pathMatch: 'full' },
      { path: 'admin', component: AdminComponent, canActivate: [LoginGuard] },
      { path: 'admin-changepassword', component: AdminChangepasswordComponent, canActivate: [LoginGuard]},
      { path: '', redirectTo: '/login', pathMatch: 'full' },
      { path: 'analysis', component: AnalysisComponent, canActivate: [LoginGuard]},
      { path: 'records', component: RecordsComponent, canActivate: [LoginGuard] },
      { path: 'filedownload/:fileUploadId', component: FiledownloadComponent, canActivate: [LoginGuard] }
    ])
  ],
  entryComponents: [
    AddAppropriatorModalComponent,
    EditAppropriatorModalComponent,
    TermsConditionsComponent,
  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [ConfigurationService], multi: true },
    DatePipe,
    { provide: OAuthStorage, useValue: localStorage },
    { provide: HTTP_INTERCEPTORS, useClass: I1, multi: true },
  ],
  bootstrap: [AppComponent]
})

export class AppModule { }
